import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import HeadMeta from '../components/HeadMeta';
import ScrollReveal from '../components/ScrollReveal';
import { useScrollZoom } from '../hooks/useScrollZoom';

export default function Contact() {
  const { lang, t } = useLanguage();
  const heroZoom = useScrollZoom({ baseScale: 1.1, maxScale: 1.4, zoomSpeed: 0.0006 });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    description: '',
    website_url: '' // Honeypot
  });
  const [files, setFiles] = useState([]);
  const [status, setStatus] = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files);
      if (selectedFiles.length > 5) {
        alert(t('contactPage.maxFilesAlert') || 'Du kan maksimalt vedhæfte 5 filer.');
        return;
      }
      setFiles(selectedFiles);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage('');

    // If honeypot is filled, silently reject
    if (formData.website_url) {
      setStatus('success');
      return;
    }

    try {
      // Send as JSON payload
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        subject: formData.subject,
        description: formData.description,
        website_url: formData.website_url,
        fileNames: files.map(f => f.name),
        fileCount: files.length,
      };

      const response = await fetch('https://api.dev.auxo.dk/fn/submit-inquiry', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const resData = await response.json();

      if (response.ok && resData.status === 'ok') {
        setStatus('success');
        setFormData({ name: '', email: '', phone: '', subject: '', description: '', website_url: '' });
        setFiles([]);
      } else {
        setStatus('error');
        setErrorMessage(resData.message || t('contactPage.errorMsg'));
      }
    } catch (err) {
      console.error('Submit error:', err);
      setStatus('error');
      setErrorMessage(t('contactPage.errorMsg'));
    }
  };

  return (
    <>
      <HeadMeta 
        title={t('contactPage.title') || 'Kontakt os'} 
        description={t('contactPage.subtitle') || 'Kontakt Captain Hurghada Tours. Send os en besked for personlig rådgivning om din næste ferie og udflugter i Hurghada.'}
      />

      {/* Hero */}
      <section className="pt-28 sm:pt-36 lg:pt-40 pb-16 bg-slate-950 text-white text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-40 overflow-hidden">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=80,format=auto/assets/086b06dd-92e0-474f-8c57-ea0b72c8dd5a/roedehavet-badebro.webp"
            alt="Captain Hurghada Kontakt"
            style={{
              transform: `scale(${heroZoom.scale}) translate3d(0, ${heroZoom.translateY}px, 0)`,
              transition: 'transform 0.1s cubic-bezier(0.1, 1, 0.1, 1)',
              willChange: 'transform',
            }}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/80 to-transparent pointer-events-none" />
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <ScrollReveal variant="up" duration={800}>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mb-6 uppercase">
              {t('contactPage.title')}
            </h1>
          </ScrollReveal>

          <ScrollReveal variant="up" delay={200} duration={800}>
            <p className="text-sm sm:text-base text-slate-300 max-w-2xl mx-auto leading-relaxed">
              {t('contactPage.subtitle')}
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-[#FAF9F5] relative">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
            
            {/* Left Col (4 cols): Contact Information */}
            <div className="lg:col-span-4 space-y-6">
              
              <div className="bg-white rounded-3xl p-8 border border-slate-200 shadow-sm space-y-6">
                <div>
                  <h3 className="text-sm font-extrabold text-slate-900 uppercase tracking-wider mb-2">
                    {t('contactPage.directContact')}
                  </h3>
                  <p className="text-slate-500 text-xs leading-relaxed">
                    {t('contactPage.directSub')}
                  </p>
                </div>

                <div className="space-y-4 pt-4 border-t border-slate-100 text-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-cyan-50 text-cyan-600 flex items-center justify-center shrink-0">
                      <i className="fa-solid fa-phone text-base" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="text-xs uppercase font-bold text-slate-400 tracking-wider">{t('contactPage.phoneText')}</p>
                      <a href="tel:+201104354994" className="text-slate-900 font-semibold hover:text-cyan-600 transition-colors block text-sm mt-0.5">
                        +20 110 435 4994 (WhatsApp)
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-cyan-50 text-cyan-600 flex items-center justify-center shrink-0">
                      <i className="fa-solid fa-envelope text-base" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="text-xs uppercase font-bold text-slate-400 tracking-wider">{t('contactPage.emailText')}</p>
                      <a href="mailto:nicolknejensen@gmail.com" className="text-slate-900 font-semibold hover:text-cyan-600 transition-colors block text-sm mt-0.5">
                        nicolknejensen@gmail.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-cyan-50 text-cyan-600 flex items-center justify-center shrink-0">
                      <i className="fa-solid fa-location-dot text-base" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="text-xs uppercase font-bold text-slate-400 tracking-wider">{t('contactPage.locationText')}</p>
                      <p className="text-slate-800 font-medium text-sm mt-0.5">{t('contactPage.locationVal')}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Instagram Card (Matching Live Site) */}
              <div className="bg-white rounded-3xl p-8 border border-slate-200 shadow-sm space-y-4">
                <div className="w-10 h-10 rounded-xl bg-cyan-50 text-cyan-600 flex items-center justify-center text-xl">
                  <i className="fa-brands fa-instagram" aria-hidden="true" />
                </div>
                <h3 className="text-sm font-extrabold text-slate-900 uppercase tracking-wider">
                  {t('contactPage.instaCardTitle')}
                </h3>
                <p className="text-slate-600 text-xs leading-relaxed">
                  {t('contactPage.instaCardDesc')}
                </p>
                <a
                  href="https://instagram.com/captainhurgada"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-slate-950 hover:bg-slate-800 text-white font-bold text-xs uppercase tracking-wider transition-colors shadow-sm w-full text-center"
                >
                  <span>{t('contactPage.instaBtn')}</span>
                  <i className="fa-solid fa-arrow-up-right-from-square text-xs" aria-hidden="true" />
                </a>
              </div>

            </div>

            {/* Right (8 cols): Booking & Inquiry Webform */}
            <div className="lg:col-span-8">
              <div className="bg-white rounded-3xl p-8 sm:p-12 border border-slate-100 shadow-xl">
                <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mb-2 uppercase tracking-tight">
                  {t('contactPage.formTitle')}
                </h2>
                <p className="text-slate-500 text-sm mb-8">
                  {t('contactPage.formSub')}
                </p>

                {status === 'success' ? (
                  <div className="p-8 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-900 text-center space-y-4 animate-in fade-in">
                    <div className="w-16 h-16 rounded-full bg-emerald-500 text-white text-3xl flex items-center justify-center mx-auto shadow-lg">
                      <i className="fa-solid fa-check" aria-hidden="true" />
                    </div>
                    <h3 className="font-bold text-2xl uppercase tracking-tight">{t('contactPage.successTitle')}</h3>
                    <p className="text-sm max-w-md mx-auto">
                      {t('contactPage.successMsg')}
                    </p>
                    <button
                      onClick={() => setStatus('idle')}
                      className="mt-4 px-6 py-2.5 rounded-full bg-emerald-700 text-white font-bold text-xs uppercase tracking-wider hover:bg-emerald-800 transition-colors cursor-pointer"
                    >
                      {t('contactPage.sendAnotherBtn')}
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    
                    {/* Mandatory Antispam Honeypot Field */}
                    <input
                      type="text"
                      name="website_url"
                      value={formData.website_url}
                      onChange={handleChange}
                      className="sr-only absolute"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
                          {t('contactPage.nameLabel')} <span className="text-rose-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          placeholder={t('contactPage.namePlaceholder')}
                          className="w-full px-4 py-3.5 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 placeholder:text-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent text-sm transition-all"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
                          {t('contactPage.phoneLabel')} <span className="text-rose-500">*</span>
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          required
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder={t('contactPage.phonePlaceholder')}
                          className="w-full px-4 py-3.5 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 placeholder:text-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent text-sm transition-all"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
                        {t('contactPage.emailLabel')} <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder={t('contactPage.emailPlaceholder')}
                        className="w-full px-4 py-3.5 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 placeholder:text-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent text-sm transition-all"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
                        {t('contactPage.subjectLabel')}
                      </label>
                      <select
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        className="w-full px-4 py-3.5 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent text-sm transition-all"
                      >
                        <option value="">{t('contactPage.subjectDefault')}</option>
                        <option value="Privat Bådtur & Skræddersyet Sejlads">{t('contactPage.subjects.privateBoat')}</option>
                        <option value="Hestetur">{t('contactPage.subjects.horseTrip')}</option>
                        <option value="Dolphin House & Snorkeltur">{t('contactPage.subjects.dolphin')}</option>
                        <option value="Ørkensafari & Quad Bike">{t('contactPage.subjects.safari')}</option>
                        <option value="Magawish / Paradise / Orange Bay / Hula Hula">{t('contactPage.subjects.islandTrip') || 'Ø-ture: Magawish / Paradise / Orange Bay / Hula Hula'}</option>
                        <option value="Speedboat">{t('contactPage.subjects.speedboat')}</option>
                        <option value="Citytour">{t('contactPage.subjects.citytour')}</option>
                        <option value="Generel rådgivning og feriepakke">{t('contactPage.subjects.general')}</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
                        {t('contactPage.descLabel')} <span className="text-rose-500">*</span>
                      </label>
                      <textarea
                        name="description"
                        required
                        rows={4}
                        value={formData.description}
                        onChange={handleChange}
                        placeholder={t('contactPage.descPlaceholder')}
                        className="w-full px-4 py-3.5 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 placeholder:text-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent text-sm transition-all"
                      />
                    </div>

                    {/* File Attachment Support */}
                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
                        {t('contactPage.fileLabel')}
                      </label>
                      <div className="border-2 border-dashed border-slate-200 rounded-xl p-4 text-center hover:border-slate-400 transition-colors bg-slate-50">
                        <input
                          type="file"
                          name="attachments"
                          multiple
                          accept=".pdf,.png,.jpg,.jpeg,.gif,.webp"
                          onChange={handleFileChange}
                          className="text-xs text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-slate-900 file:text-white hover:file:bg-slate-800 cursor-pointer"
                        />
                        <p className="text-[11px] text-slate-400 mt-2">
                          {t('contactPage.fileFormats')}
                        </p>
                      </div>
                    </div>

                    {status === 'error' && (
                      <div className="p-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-sm flex items-center gap-3">
                        <i className="fa-solid fa-circle-exclamation text-rose-500" aria-hidden="true" />
                        <span>{errorMessage}</span>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={status === 'submitting'}
                      className="w-full py-4 rounded-xl bg-slate-950 hover:bg-slate-800 text-white font-extrabold text-sm uppercase tracking-wider transition-all duration-300 shadow-md flex items-center justify-center gap-3 cursor-pointer disabled:opacity-50"
                    >
                      {status === 'submitting' ? (
                        <>
                          <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
                          <span>{t('contactPage.submittingBtn')}</span>
                        </>
                      ) : (
                        <>
                          <span>{t('contactPage.submitBtn')}</span>
                          <i className="fa-solid fa-paper-plane" aria-hidden="true" />
                        </>
                      )}
                    </button>

                  </form>
                )}

              </div>
            </div>

          </div>

        </div>
      </section>
    </>
  );
}
